import { Button, Grid, Paper, Stack, Text } from "@mantine/core";
import type React from "react";
import { useParams } from "wouter";
import { client } from "@/api-client";
import { FancyLoader } from "@/components/FancyLoader";
import { Header } from "@/components/Header";
import { TrainerRoutes, UniversalRoutes, useNavigator } from "@/routers/routes";
import { FilePreview } from "../universal/FilePreview";

export const FilePreviewForChat: React.FC = () => {
  const { fileId, engagementId } = useParams<{
    fileId: string;
    engagementId: string;
  }>();
  const { navigate } = useNavigator();
  const { data: file } = client.useQuery("get", `/universal/files/{fileId}`, {
    params: {
      path: {
        fileId,
      },
    },
  });
  const sendMessage = client.useMutation(
    "post",
    "/universal/chats/{engagementId}/messages",
  );

  const sendToChat = async () => {
    await sendMessage.mutateAsync({
      params: {
        path: {
          engagementId,
        },
      },
      body: {
        message: file?.name,
        objectId: fileId,
        specialType: "FILE",
      },
    });
    navigate({
      path: UniversalRoutes.CHAT.DETAIL,
      params: { engagementId },
    });
  };

  return (
    <Stack>
      <FilePreview />
      <Button
        disabled={!file}
        onClick={sendToChat}
        loading={sendMessage.isPending}
      >
        Send to chat
      </Button>
    </Stack>
  );
};

// Helper function to render content inside the Container
export const FilePicker: React.FC = () => {
  const { engagementId } = useParams<{ engagementId: string }>();
  const { navigator } = useNavigator();
  const listFiles = client.useQuery(
    "get",
    "/universal/files/",
    {},
    {
      staleTime: 0,
      gcTime: 0,
    },
  );

  if (!listFiles.data) {
    return <FancyLoader />;
  }

  return (
    <Stack>
      <Header title={`Pick a file`} />
      {listFiles.data.files.map((file, index) => (
        <Paper
          key={index}
          p="md"
          pt="xs"
          pb="xs"
          radius="md"
          withBorder
          onClick={navigator({
            path: TrainerRoutes.CHAT.FILE_PREVIEW,
            params: {
              engagementId,
              fileId: file.id,
            },
          })}
        >
          <Grid>
            <Grid.Col span={8}>
              <Text size="lg" mb={5}>
                {file.name}
              </Text>
              <Text size="sm" c="dimmed" mb={5}>
                {file.description}
              </Text>
              <Text size="xs" c="blue">
                Type: {file.type}
              </Text>
            </Grid.Col>
          </Grid>
        </Paper>
      ))}
    </Stack>
  );
};
